import { useState } from "react";
import { EyeIcon, EyeOffIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "../ui/button";
import type { InputProps } from "../ui/input";
import { InputGroup, InputGroupAddon, InputGroupInput } from "../ui/input-group";

export type PasswordInputProps = Omit<InputProps, "type">;

/**
 * Password field with a reveal toggle, used by both account forms.
 *
 * A masked field answers a typo with the same message as a wrong password, so
 * the user retypes a password that was never wrong. Revealing is the only way
 * they can tell those apart before submitting.
 *
 * The toggle is a `Button`, which defaults to `type="button"`: a bare `button`
 * inside a form submits it, so revealing the password would attempt a sign-in.
 *
 * `type="text"` re-enables the text-entry helpers `type="password"` suppresses,
 * so a mobile keyboard would capitalize the first letter of a revealed password
 * or autocorrect it into another word — silently changing the credential being
 * typed. They are switched off explicitly, in both states.
 */
export function PasswordInput(props: PasswordInputProps) {
  const { t } = useTranslation();
  const [revealed, setRevealed] = useState(false);

  return (
    <InputGroup>
      <InputGroupInput
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        type={revealed ? "text" : "password"}
        {...props}
      />
      <InputGroupAddon align="inline-end">
        <Button
          aria-label={revealed ? t("auth/hidePassword_lzyxl0") : t("auth/showPassword_ydsvul")}
          onClick={() => setRevealed((current) => !current)}
          size="icon-xs"
          variant="ghost"
        >
          {revealed ? <EyeOffIcon /> : <EyeIcon />}
        </Button>
      </InputGroupAddon>
    </InputGroup>
  );
}
